/**
 * Компас с привязкой по якорю (фаза ОРИЕНТАЦИЯ, спека 05 v2).
 *
 * Механика: магнитометру АБСОЛЮТНО не доверяем (внутри зданий врёт) — берём
 * только ОТНОСИТЕЛЬНОЕ вращение устройства от момента привязки. Абсолют даёт
 * якорь: пользователь поворачивается лицом к щиту и жмёт кнопку — в этот момент
 * его взгляд = anchor.heading (авторские данные карты). offset = heading − показание;
 * дальше world = показание + offset, как ни верти телефон.
 *
 * Датчики: iOS — DeviceOrientationEvent.requestPermission() строго в обработчике
 * тапа, webkitCompassHeading (уже по часовой от севера); Android —
 * deviceorientationabsolute с fallback на deviceorientation, heading = 360 − alpha
 * (alpha по W3C растёт против часовой). Поворот экрана компенсируется углом
 * screen.orientation.
 */
import { bearingDeg, bearingDiffDeg, haversineM, type LonLat } from '@app/geo'

/** Показание ориентации: азимут «куда смотрит верх телефона», по часовой от севера. */
export interface OrientationReading {
  readonly headingDeg: number
  /** Точность компаса ОС (iOS webkitCompassAccuracy), если известна. */
  readonly accuracyDeg: number | null
  readonly absolute: boolean
  readonly timestampMs: number
}

export type SensorAccess = 'granted' | 'denied' | 'unsupported'

/** Нормализация угла в [0, 360). */
export function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360
}

/**
 * Показание события → компас-азимут (по часовой от севера) с учётом поворота
 * экрана. Возвращает null, если у события нет данных вращения.
 */
export function headingFromEvent(
  alpha: number | null,
  webkitCompassHeading: number | undefined,
  screenAngleDeg: number,
): number | null {
  if (typeof webkitCompassHeading === 'number' && Number.isFinite(webkitCompassHeading)) {
    return normalizeDeg(webkitCompassHeading + screenAngleDeg)
  }
  if (typeof alpha === 'number' && Number.isFinite(alpha)) {
    return normalizeDeg(360 - alpha + screenAngleDeg)
  }
  return null
}

/** Привязка: пользователь смотрит на щит → его взгляд = anchor.heading. */
export function bindOffset(anchorHeadingDeg: number, deviceHeadingDeg: number): number {
  return normalizeDeg(anchorHeadingDeg - deviceHeadingDeg)
}

/** Мировой азимут взгляда после привязки. */
export function applyOffset(deviceHeadingDeg: number, offsetDeg: number): number {
  return normalizeDeg(deviceHeadingDeg + offsetDeg)
}

/** Угол поворота стрелки на экране: куда цель относительно взгляда. */
export function arrowAngleDeg(targetBearingDeg: number, worldHeadingDeg: number): number {
  return normalizeDeg(targetBearingDeg - worldHeadingDeg)
}

export type Turn = 'straight' | 'right' | 'back' | 'left'

/**
 * Направление первого движения относительно взгляда «лицом к щиту»
 * (anchor.heading = взгляд читающего щит; сектора ±45°).
 */
export function turnFromBoard(firstLegBearingDeg: number, anchorHeadingDeg: number): Turn {
  const diff = bearingDiffDeg(anchorHeadingDeg, firstLegBearingDeg)
  if (diff >= -45 && diff <= 45) return 'straight'
  if (diff > 45 && diff < 135) return 'right'
  if (diff < -45 && diff > -135) return 'left'
  return 'back'
}

export interface FirstLeg {
  readonly bearingDeg: number
  readonly lengthM: number
}

const TURN_THRESHOLD_DEG = 25
/** Вводные сегменты «пристёгивания» точки старта к графу курсом не считаются. */
const LEAD_IN_MAX_M = 8

/**
 * Первое «плечо» маршрута: азимут старта и дистанция до первого поворота
 * (устойчивое отклонение курса > 25°). Азимут — от первого ЗНАЧИМОГО сегмента:
 * микро-сегмент от якоря до ближайшего узла сети не является направлением
 * движения и не должен давать фразу «выезд налево — 4 м».
 */
export function firstLeg(polyline: readonly LonLat[]): FirstLeg | null {
  if (polyline.length < 2) return null
  const { startIdx, leadM } = skipLeadIn(polyline)
  const a = polyline[startIdx]
  const b = polyline[startIdx + 1]
  if (!a || !b) return null
  const start = bearingDeg(a, b)
  let length = leadM + haversineM(a, b)
  for (let i = startIdx + 1; i < polyline.length - 1; i++) {
    const p = polyline[i]
    const q = polyline[i + 1]
    if (!p || !q) break
    if (Math.abs(bearingDiffDeg(start, bearingDeg(p, q))) > TURN_THRESHOLD_DEG) break
    length += haversineM(p, q)
  }
  return { bearingDeg: start, lengthM: Math.round(length) }
}

/** Индекс первого значимого сегмента и длина пропущенного «пристёгивания». */
function skipLeadIn(polyline: readonly LonLat[]): { startIdx: number; leadM: number } {
  let leadM = 0
  let startIdx = 0
  while (startIdx + 2 < polyline.length && leadM < LEAD_IN_MAX_M) {
    const p = polyline[startIdx]
    const q = polyline[startIdx + 1]
    if (!p || !q) break
    const seg = haversineM(p, q)
    if (seg >= LEAD_IN_MAX_M) break
    leadM += seg
    startIdx++
  }
  return { startIdx, leadM }
}

interface OrientationEventLike extends Event {
  readonly alpha: number | null
  readonly absolute?: boolean
  readonly webkitCompassHeading?: number
  readonly webkitCompassAccuracy?: number
}

type PermissionRequester = { requestPermission?: () => Promise<'granted' | 'denied'> }

/** Привязка компаса, переносимая между фазами (orient → AR-ведение). */
export interface OrientationBinding {
  readonly offsetDeg: number
  readonly boundAtMs: number
}

const STALE_AFTER_MS = 90_000

/**
 * Живой компас: подписка на датчики + привязка offset по якорю.
 * Абсолютные события предпочитаются относительным (Android шлёт оба).
 */
export class OrientationService {
  private reading: OrientationReading | null = null
  private offsetDeg: number | null = null
  private boundAtMs: number | null = null
  private onReading: ((r: OrientationReading) => void) | null = null
  private absoluteSeen = false
  private readonly listener = (event: Event): void =>
    this.handleEvent(event as OrientationEventLike, false)
  private readonly listenerAbs = (event: Event): void =>
    this.handleEvent(event as OrientationEventLike, true)

  /** Запуск строго из обработчика тапа (iOS-разрешение живёт только там). */
  async start(onReading: (r: OrientationReading) => void): Promise<SensorAccess> {
    if (typeof DeviceOrientationEvent === 'undefined') return 'unsupported'
    this.onReading = onReading
    const requester = DeviceOrientationEvent as unknown as PermissionRequester
    if (typeof requester.requestPermission === 'function') {
      try {
        if ((await requester.requestPermission()) !== 'granted') return 'denied'
      } catch {
        return 'denied'
      }
    }
    window.addEventListener('deviceorientationabsolute', this.listenerAbs)
    window.addEventListener('deviceorientation', this.listener)
    return 'granted'
  }

  private handleEvent(event: OrientationEventLike, absolute: boolean): void {
    // Абсолютный поток надёжнее: как только он появился — относительный глушим.
    if (this.absoluteSeen && !absolute && event.webkitCompassHeading === undefined) return
    if (absolute) this.absoluteSeen = true
    const screenAngle = typeof screen.orientation?.angle === 'number' ? screen.orientation.angle : 0
    const heading = headingFromEvent(event.alpha, event.webkitCompassHeading, screenAngle)
    if (heading === null) return
    this.reading = {
      headingDeg: heading,
      accuracyDeg:
        typeof event.webkitCompassAccuracy === 'number' && event.webkitCompassAccuracy >= 0
          ? event.webkitCompassAccuracy
          : null,
      absolute: absolute || event.webkitCompassHeading !== undefined,
      timestampMs: Date.now(),
    }
    this.onReading?.(this.reading)
  }

  /** Привязка по якорю: текущий взгляд = anchor.heading. false — показаний ещё нет. */
  bind(anchorHeadingDeg: number): boolean {
    if (!this.reading) return false
    this.offsetDeg = bindOffset(anchorHeadingDeg, this.reading.headingDeg)
    this.boundAtMs = Date.now()
    return true
  }

  /** Текущая привязка — для переноса в следующую фазу (orient → AR-ведение). */
  get binding(): OrientationBinding | null {
    return this.offsetDeg !== null && this.boundAtMs !== null
      ? { offsetDeg: this.offsetDeg, boundAtMs: this.boundAtMs }
      : null
  }

  /** Восстановить привязку из другой фазы (возраст сохраняется — честный stale). */
  restoreBinding(binding: OrientationBinding): void {
    this.offsetDeg = binding.offsetDeg
    this.boundAtMs = binding.boundAtMs
  }

  /** Поправка привязки (напр. по GPS-курсу на ходу): offset += deltaDeg. */
  adjustOffset(deltaDeg: number): void {
    if (this.offsetDeg === null) return
    this.offsetDeg = normalizeDeg(this.offsetDeg + deltaDeg)
    this.boundAtMs = Date.now()
  }

  get bound(): boolean {
    return this.offsetDeg !== null
  }

  /** Мировой азимут взгляда (null — нет привязки или показаний). */
  worldHeading(): number | null {
    if (this.reading === null || this.offsetDeg === null) return null
    return applyOffset(this.reading.headingDeg, this.offsetDeg)
  }

  /** Честность бейджа: гиро дрейфует — спустя минуты привязка «устаревает». */
  isStale(): boolean {
    return this.boundAtMs !== null && Date.now() - this.boundAtMs > STALE_AFTER_MS
  }

  stop(): void {
    window.removeEventListener('deviceorientationabsolute', this.listenerAbs)
    window.removeEventListener('deviceorientation', this.listener)
    this.reading = null
    this.offsetDeg = null
    this.boundAtMs = null
    this.onReading = null
    this.absoluteSeen = false
  }
}
